import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutGrid, Users, KanbanSquare, UploadCloud, LogOut, BarChart3, ShieldCheck, BookOpen, UserCog, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import NotificationBell from '@/components/NotificationBell';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutGrid },
    { path: '/leads', label: 'I Miei Lead', icon: Users },
    ...(isAdmin ? [{ path: '/admin/leads', label: 'Tutti i Lead', icon: ShieldCheck, adminOnly: true }] : []),
    ...(isAdmin ? [{ path: '/admin/team', label: 'Team Commerciali', icon: UserCog, adminOnly: true }] : []),
    ...(isAdmin ? [{ path: '/admin/monitor', label: 'Monitor Pipeline', icon: Eye, adminOnly: true }] : []),
    { path: '/pipeline', label: 'Pipeline', icon: KanbanSquare },
    { path: '/reports', label: 'Report', icon: BarChart3 },
    { path: '/import', label: 'Importa', icon: UploadCloud },
    { path: '/guide', label: 'Guida', icon: BookOpen }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-512.png" alt="360 Community" className="h-8 w-8 rounded-lg" />
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Lead Manager</h1>
          </div>
          <NotificationBell />
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  isActive(item.path)
                    ? item.adminOnly ? 'bg-indigo-100 text-indigo-700' : 'bg-indigo-50 text-indigo-600'
                    : item.adminOnly ? 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50' : 'text-slate-600 hover:text-indigo-600 hover:bg-indigo-50/50'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="h-5 w-5" />
                {item.label}
                {item.adminOnly && <span className="ml-auto text-xs bg-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded">Admin</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3 px-2">
            <Avatar className="h-10 w-10 bg-indigo-100">
              <AvatarFallback className="bg-indigo-100 text-indigo-600 font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Esci
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}